import {
  useEffect,
  useId,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { useI18n } from "./i18n";
import { usePortal } from "./state";

/** An in-app link: a real `<a href>` (middle-click, copy link) that routes
 *  without a reload on a plain click. */
export function Link(
  props: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }
) {
  const { to, onClick, ...rest } = props;
  const { go } = usePortal();
  return (
    <a
      {...rest}
      href={to}
      onClick={(e) => {
        onClick?.(e);
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey
        ) {
          return;
        }
        e.preventDefault();
        go(to);
      }}
    />
  );
}

/** A labelled input with an optional hint and error, wired for screen readers. */
export function Field(
  props: InputHTMLAttributes<HTMLInputElement> & {
    label: string;
    hint?: string;
    error?: string | null;
  }
) {
  const { label, hint, error, id, ...input } = props;
  const auto = useId();
  const inputId = id ?? auto;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        aria-describedby={
          [hintId, errorId].filter(Boolean).join(" ") || undefined
        }
        aria-invalid={error ? true : undefined}
        {...input}
      />
      {hint && (
        <p id={hintId} className="field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}

/** Status text. `alert` for failures (announced at once), `status` otherwise. */
export function Notice(props: {
  kind?: "info" | "success" | "error";
  children: ReactNode;
}) {
  const kind = props.kind ?? "info";
  return (
    <div
      className={`notice notice--${kind}`}
      role={kind === "error" ? "alert" : "status"}
    >
      {props.children}
    </div>
  );
}

export function Loading() {
  const { t } = useI18n();
  return (
    <p className="loading" role="status">
      {t("common.loading")}
    </p>
  );
}

/**
 * A page heading that takes focus on mount, so a route change is announced.
 *
 * Except while the header search has focus: typing a query on another page
 * opens the store, and moving focus to its heading would snatch the box out
 * from under the person still typing.
 */
export function PageTitle(props: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('[role="search"]')) {
      return;
    }
    ref.current?.focus({ preventScroll: true });
  }, []);
  return (
    <h1
      className={
        props.className ? `page-title ${props.className}` : "page-title"
      }
      tabIndex={-1}
      ref={ref}
    >
      {props.children}
    </h1>
  );
}

/**
 * Title artwork. Lists use the wide 16:9 card; a title page the tall poster.
 * An image that fails to load becomes the same quiet placeholder as no image,
 * never a broken-image glyph.
 */
export function Poster(props: {
  src?: string;
  title: string;
  shape?: "portrait" | "landscape";
}) {
  const [failed, setFailed] = useState(false);
  const cls =
    props.shape === "landscape" ? "poster poster--landscape" : "poster";
  return props.src && !failed ? (
    <img
      className={cls}
      src={props.src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  ) : (
    <span className={`${cls} poster--empty`} aria-hidden="true">
      <span className="poster__initial">
        {Array.from(props.title.trim())[0] ?? ""}
      </span>
    </span>
  );
}

/** A grey block where a card will be, the same size, so nothing jumps. */
export function SkeletonCards(props: { count?: number }) {
  return (
    <>
      {Array.from({ length: props.count ?? 6 }, (_, i) => (
        <li
          key={i}
          className="title-card title-card--skeleton"
          aria-hidden="true"
        >
          <span className="skeleton skeleton--media" />
          <span className="skeleton skeleton--line" />
          <span className="skeleton skeleton--line skeleton--short" />
        </li>
      ))}
    </>
  );
}

/** An empty state that says what happened and offers the next step. */
export function EmptyState(props: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {props.icon && (
        <span className="empty__icon" aria-hidden="true">
          {props.icon}
        </span>
      )}
      <p className="empty__title">{props.title}</p>
      {props.children && <div className="empty__body">{props.children}</div>}
      {props.action && <div className="empty__action">{props.action}</div>}
    </div>
  );
}
